<template>
  <div>
    <h4>AET Solicitada — status dos robôs</h4>
    <p style="margin-top: 0; color: #757575; font-size: 0.875rem">
      As AETs estão sendo processadas pelos robôs nos órgãos. Erros aparecem
      destacados; cada licença com erro pode ser reenviada individualmente após
      corrigir o que for necessário em
      <em>Licenças → editar</em>.
    </p>

    <div v-if="!stateReady" class="p-text-center" style="padding: 2rem">
      <ProgressSpinner style="width: 36px; height: 36px" />
    </div>

    <search-result
      v-else
      :showSelector="false"
      :results="resultsStatus"
      :loading="stateLoading"
      :hidePagination="true"
      :hideActions="true"
    >
      <template v-slot:title>
        {{ statusList.length }} licença(s) — {{ qtdErros }} com erro
      </template>
      <template v-slot:columns>
        <Column field="placa" header="Placa" style="width: 8rem" />
        <Column field="siglaOrgao" header="Órgão" style="width: 8rem" />
        <Column header="Status">
          <template #body="slotProps">
            <span
              v-if="slotProps.data.temErro"
              class="status-erro"
              v-text="'Erro'"
            />
            <span
              v-else-if="slotProps.data.flSituacaoOrgao === 'OK'"
              class="status-ok"
              v-text="'Concluída'"
            />
            <span
              v-else
              class="status-andamento"
              v-text="formatStatus(slotProps.data.statusRobo) || 'Aguardando'"
            />
          </template>
        </Column>
        <Column header="Mensagem" style="min-width: 20rem">
          <template #body="slotProps">
            <span v-text="slotProps.data.msgCliente || '—'" />
          </template>
        </Column>
        <Column :exportable="false" style="width: 14rem; text-align: right">
          <template #body="slotProps">
            <button-group>
              <Button
                v-if="slotProps.data.temErro"
                label="Reenviar"
                icon="pi pi-refresh"
                class="p-button-rounded p-button-warning p-button-sm"
                v-title="'Reenvia esta licença ao robô'"
                @click="reenviar(slotProps.data)"
              />
              <Button
                icon="pi pi-external-link"
                class="p-button-rounded p-button-text p-button-sm"
                v-title="'Abrir licença'"
                @click="abrirLicenca(slotProps.data.idLicenca)"
              />
            </button-group>
          </template>
        </Column>
      </template>
    </search-result>

    <div class="p-col-12" style="margin-top: 1rem; text-align: right">
      <Button
        label="Atualizar"
        icon="pi pi-sync"
        class="p-button-rounded p-button-text"
        @click="carregarStatus"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref,
  PropType
} from "vue";
import { useRouter } from "vue-router";
import { useToast, useConfirm } from "../../modules/message";
import {
  useOrcamentoService,
  StatusRenovacaoLicencaPayload
} from "../../modules/business/orcamento/orcamentoBusiness";

interface StatusState {
  statusList: Array<StatusRenovacaoLicencaPayload>;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props) {
    const router = useRouter();
    const state = reactive<StatusState>({
      statusList: [],
      stateReady: false,
      loadings: []
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const qtdErros = computed(
      () => state.statusList.filter((s) => s.temErro).length
    );

    const resultsStatus = computed(() => ({
      content: state.statusList,
      page: {
        size: state.statusList.length,
        number: 0,
        totalElements: state.statusList.length,
        totalPages: 1
      }
    }));

    const carregarStatus = () => {
      const { getStatusLicencas, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      getStatusLicencas(props.idOrcamento)
        .then(() => {
          state.statusList =
            (data.value as Array<StatusRenovacaoLicencaPayload>) ?? [];
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const reenviar = (status: StatusRenovacaoLicencaPayload) => {
      useConfirm().require({
        message: `Reenviar a licença da placa ${status.placa} ao robô?`,
        header: "Confirmar reenvio",
        icon: "pi pi-info-circle",
        accept: () => {
          const { reenviarLicencaOrgao, loading } = useOrcamentoService();
          state.loadings.push(loading);
          reenviarLicencaOrgao(props.idOrcamento, status.idLicencaOrgao)
            .then(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              useToast().success("Reenvio agendado");
              carregarStatus();
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        }
      });
    };

    const abrirLicenca = (idLicenca: number) => {
      const url = router.resolve({
        name: "licenca-edit",
        params: { idLicenca: idLicenca + "" }
      }).fullPath;
      window.open(url, "_blank");
    };

    const formatStatus = (status?: string | null): string => {
      if (!status) return "";
      return status.replaceAll("_", " ").toLowerCase();
    };

    onMounted(() => carregarStatus());

    return {
      qtdErros,
      resultsStatus,
      carregarStatus,
      reenviar,
      abrirLicenca,
      formatStatus,
      stateLoading,
      ...toRefs(state)
    };
  },
  props: {
    idOrcamento: {
      type: Number,
      required: true
    },
    parentLoading: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  }
});
</script>

<style scoped lang="scss">
.status-erro {
  color: #c62828;
  font-weight: 600;
  background: #ffebee;
  padding: 0.125rem 0.5rem;
  border-radius: 8px;
}
.status-ok {
  color: #2e7d32;
  font-weight: 600;
  background: #e8f5e9;
  padding: 0.125rem 0.5rem;
  border-radius: 8px;
}
.status-andamento {
  color: #1565c0;
  font-weight: 500;
  text-transform: capitalize;
}
</style>
